import * as _ from 'lodash';

const paramRegex = new RegExp(/:.[^\/]*/);

export const buildRoute = (path: string, params: Array<string>) => {
  let builded = path;
  for (const param of params) {
    builded = builded.replace(paramRegex, param);
  }
  return builded;
};

export const buildQuery = (params: any) =>
  Object.entries(params)
    .reduce((result, [key, value]) => {
      if (_.isArray(value)) (<any>value).forEach((v: any) => result.push([key, v]));
      else result.push([key, value]);
      return result;
    }, [])
    .map(([key, value]) => (value ? `${key}=${value}` : null))
    .filter((value) => value != null)
    .join('&');

export const buildOrder = (params: any) =>
  Object.entries(params)
    .map(([key, value]) => (value ? `${key}:${value}` : null))
    .filter((value) => value != null)
    .join(',');

export const buildAddressString = (address: { street?: string; postalCode?: string; city?: string; country?: { name: string; country: string } }): string => {
  const addressParts = [];

  if (address.street) addressParts.push(address.street);
  if (address.city) addressParts.push(`${address.postalCode ? `${address.postalCode} ` : ''}${address.city}`);
  if (address.country) addressParts.push(address.country.name);

  return addressParts.join(', ');
};

export const generalizePath = (path: string): string => {
  return path.replace(/\[(\d+|\?)\]/g, '');
};

export const normalizePath = (path: string): string => {
  return path.replace(/\[/g, '.').replace(/\]/g, '');
};

export const buildPath = (object: any, path: string): Array<string> => {
  let resolvedSegments: Array<string> = [''];

  const segments = path.split('[?]');
  const maxSegmentIndex = segments.length - 1;

  segments.forEach((segment, segmentIndex) => {
    if (!segment) return;

    const newResolvedSegments: Array<string> = [];

    resolvedSegments.forEach((resolvedSegment) => {
      const currentPath = resolvedSegment + segment;
      const currentObject = _.get(object, currentPath);
      if (segmentIndex < maxSegmentIndex && _.isFinite(currentObject?.length)) {
        (<Array<any>>currentObject).forEach((item, index) => newResolvedSegments.push(`${currentPath}[${index}]`));
        return;
      }
      newResolvedSegments.push(currentPath);
    });
    resolvedSegments = newResolvedSegments;
  });

  return resolvedSegments;
};

export const recursiveSumBy = (tree: any, iteratee = (path: string, value: any) => 1): number => {
  const recursion = (path: string, node: any): number => {
    const sum = iteratee(path, node);
    if (_.isArrayLikeObject(node) && node.length) {
      return (<Array<any>>node).reduce((acc, cur, idx) => acc + recursion(path ? `${path}.${idx}` : `${idx}`, cur), sum);
    }
    if (_.isObjectLike(node) && !_.isEmpty(node)) {
      return Object.entries(node).reduce((acc, [key, cur]) => acc + recursion(path ? `${path}.${key}` : key, cur), sum);
    }
    return sum;
  };

  return recursion('', tree);
};

export const recursiveEveryBy = (tree: any, iteratee = (path: string, value: any) => !!value): boolean => {
  const recursion = (path: string, node: any): boolean => {
    const result = iteratee(path, node);
    if (!result) return false;

    if (_.isArrayLikeObject(node) && node.length) {
      return (<Array<any>>node).every((cur, idx) => recursion(path ? `${path}.${idx}` : `${idx}`, cur));
    }
    if (_.isObjectLike(node) && !_.isEmpty(node)) {
      return Object.entries(node).every(([key, cur]) => recursion(path ? `${path}.${key}` : key, cur));
    }
    return true;
  };

  return recursion('', tree);
};

export const omitEx = (object: any, paths: Array<string>) => {
  const resolvedPaths: Array<string> = [];

  paths.forEach((path) => resolvedPaths.push(...buildPath(object, path)));

  return _.omit(object, resolvedPaths);
};

export const search = (text: string, str: string, options = { exactMatch: false, caseSensitive: false }) => {
  if (text == null) return false;
  if (str == null) return false;

  const preparedText = options.caseSensitive ? text : text.toLowerCase();
  const preparedStr = options.caseSensitive ? str : str.toLowerCase();

  if (options.exactMatch) return preparedText === preparedStr;
  return preparedText.search(preparedStr) >= 0;
};

export const roundUp = (value: number, fractionDigits: number = 2): number => {
  const multiplier = 10 ** fractionDigits;

  return Math.ceil(value * multiplier) / multiplier;
};

export const round = (value: number, fractionDigits: number = 2): number => {
  const multiplier = 10 ** fractionDigits;

  return Math.round(value * multiplier) / multiplier;
};

export const roundDown = (value: number, fractionDigits: number = 2): number => {
  const multiplier = 10 ** fractionDigits;

  return Math.floor(value * multiplier) / multiplier;
};

export default {
  buildRoute,
  buildQuery,
  buildOrder,
  buildAddressString,
  buildPath,
  generalizePath,
  normalizePath,
  recursiveSumBy,
  recursiveEveryBy,
  omitEx,
  search,
  roundUp,
  round,
  roundDown,
};
